import { useState } from 'react';
import type { DraftState, Manager } from '../types';
import { sounds } from '../lib/soundEffects';

interface Props {
  state: DraftState;
  localManager: Manager | null;
  isCommissioner: boolean;
  onSwitchUser: () => void;
  leagueId: string;
  onSwitchLeague: () => void;
  onOpenWarRoom?: () => void;
  onOpenSettings?: () => void;
  onOpenRecap2025?: () => void;
}

export function Header({
  state, localManager, isCommissioner, onSwitchUser, leagueId, onSwitchLeague,
  onOpenWarRoom, onOpenSettings, onOpenRecap2025,
}: Props) {
  const [muted, setMuted] = useState(() => sounds.isMuted());
  const nominee = state.managers[state.currentNomineeIndex];
  const buildDate = new Date(__BUILD_TIME__);
  const buildLabel = isNaN(buildDate.getTime())
    ? __BUILD_TIME__
    : buildDate.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  const leagueLabel = state.leagueName ?? (leagueId === 'default' ? 'Original League' : leagueId);
  const copyShareLink = async () => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('league', leagueId);
      await navigator.clipboard.writeText(url.toString());
    } catch { /* clipboard unavailable */ }
  };

  const handleToggleSound = () => {
    const next = sounds.toggleMute();
    setMuted(next);
  };

  return (
    <header className="panel p-4 mb-6 flex flex-wrap items-center gap-4 justify-between">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl md:text-3xl font-extrabold bg-gradient-to-r from-teal-300 via-cyan-300 to-sky-400 bg-clip-text text-transparent">
            🏈 NFL Auction {state.currentDraftYear}
          </h1>
          <span className="pill bg-cyan-950 border border-cyan-500/50 text-cyan-300 text-[10px] font-bold">
            AI ENGINE 2026
          </span>
        </div>
        <p className="text-sm text-slate-400">
          {state.phase === 'Finished' ? 'Draft complete!' : `Nominating: `}
          {state.phase !== 'Finished' && (
            <span className="font-bold text-teal-300">{nominee?.emoji} {nominee?.name}</span>
          )}
        </p>
        <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-500">
          <span className="pill bg-slate-800 border border-slate-700">
            League: <span className="text-slate-200 font-semibold">{leagueLabel}</span>
            <span className="text-slate-500"> ({leagueId})</span>
          </span>
          <button onClick={copyShareLink} className="text-teal-400 hover:underline" title="Copy shareable link">
            copy link
          </button>
          <button onClick={onSwitchLeague} className="text-slate-400 hover:underline">
            switch league
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 md:gap-3">
        {/* 2025 Season Recap Button */}
        {onOpenRecap2025 && (
          <button
            onClick={onOpenRecap2025}
            className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-yellow-400 hover:from-amber-400 hover:to-yellow-300 text-slate-950 font-black text-xs shadow-lg shadow-amber-500/20 active:scale-95 transition flex items-center gap-1.5"
            title="View 2025 Season Recap & Championship Standings"
          >
            <span>🏆</span>
            <span>2025 Recap (David Won)</span>
          </button>
        )}

        {/* AI War Room Button */}
        {onOpenWarRoom && (
          <button
            onClick={onOpenWarRoom}
            className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyan-600 to-teal-500 hover:from-cyan-500 hover:to-teal-400 text-slate-950 font-black text-xs shadow-lg shadow-cyan-500/20 active:scale-95 transition flex items-center gap-1.5"
            title="Open Rob's AI Commander War Room"
          >
            <span>🧠</span>
            <span>Rob's AI War Room</span>
          </button>
        )}

        {/* AI Settings Button */}
        {onOpenSettings && (
          <button
            onClick={onOpenSettings}
            className="p-1.5 px-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs border border-slate-700"
            title="AI Engine & LLM Settings"
          >
            ⚙️ AI Settings
          </button>
        )}

        {/* Audio Mute/Unmute */}
        <button
          onClick={handleToggleSound}
          className="p-1.5 px-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs border border-slate-700"
          title={muted ? 'Unmute draft sound cues' : 'Mute draft sound cues'}
        >
          {muted ? '🔇' : '🔊'}
        </button>

        {localManager && (
          <div className="text-right hidden sm:block">
            <div className="text-xs text-slate-400">Signed in as</div>
            <div className="font-bold text-white">{localManager.emoji} {localManager.name}</div>
          </div>
        )}
        {isCommissioner && (
          <span className="pill bg-rose-600 text-white">🛡 Commissioner</span>
        )}
        <button onClick={onSwitchUser} className="btn-ghost text-xs">Switch User</button>
        <span
          className="pill bg-slate-800 text-slate-300 border border-slate-700 text-[10px] font-mono hidden md:inline-flex"
          title={`Built ${buildLabel}`}
        >
          v{__APP_VERSION__}
        </span>
      </div>
    </header>
  );
}
